import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import {
  cn,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  useFilter,
} from '@rewardops/ui'

import { replacePlaceholders } from '~/utils/translation-utils'
import { MapleSelect } from '~/components/maple/components/select'
import ResponsiveComponent from '~/components/responsive-component'
import { styling } from '~/components/styling'
import { useTranslation } from '~/app/i18n/client'
import { DOTS, usePaginationSelector } from '../hooks/use-pagination-selector'

export const PaginationInput = ({
  className,
  totalNumberOfItems,
  sortOption,
  itemsPerPageOptions = [24, 48, 96],
}: {
  className?: string
  totalNumberOfItems?: number
  sortOption?: string
  itemsPerPageOptions?: number[]
}) => {
  const { t } = useTranslation()
  const { currentPage, setCurrentPage, itemsPerPage, setPageInfo } = useFilter()

  const paginationRange = usePaginationSelector({
    currentPage,
    totalCount: totalNumberOfItems ? totalNumberOfItems : 0,
    siblingCount: 1,
    pageSize: itemsPerPage,
    simplified: true,
  })

  if (!totalNumberOfItems) {
    return null
  }

  let lastPage = paginationRange
    ? paginationRange[paginationRange.length - 1]
    : 1

  const onNext = () => {
    if (currentPage === lastPage) return
    setCurrentPage(currentPage + 1)
  }

  const onPrevious = () => {
    if (currentPage === 1) return
    setCurrentPage(currentPage - 1)
  }

  const desktop = (
    <div className={cn('flex items-center justify-end gap-32', className)}>
      <MapleSelect
        id="pagination-items-per-page-select"
        className="w-fit"
        placeholder={replacePlaceholders(t('general.showing'), {
          itemsCount: itemsPerPage ?? 24,
        })}
        defaultValue={
          itemsPerPage?.toString() ?? itemsPerPageOptions[0]?.toString()
        }
        options={itemsPerPageOptions.map((value) => ({
          key: value.toString(),
          value: replacePlaceholders(t('general.showing'), {
            itemsCount: value,
          }),
        }))}
        onValueChange={(value) => {
          setPageInfo(1, parseInt(value), sortOption)
        }}
      />

      <div>
        <Pagination>
          <PaginationContent className="flex items-center gap-2.5 -space-x-px">
            <PaginationItem
              id="pagination-previous"
              key="previous"
              className={cn(styling.pagination.cheveron, {
                [styling.pagination.disabledPaginationItem]: currentPage === 1,
              })}
              onClick={onPrevious}
            >
              <ChevronLeft size={24} />
            </PaginationItem>
            {paginationRange?.map((pageNumber, index) => {
              if (pageNumber === DOTS) {
                // There should only be at most 2 ellipsis at the same index so this won't break react key requirement
                return (
                  <PaginationItem
                    key={`${DOTS}-${index}`}
                    className={cn(styling.pagination.ellipsis, 'px-0')}
                  >
                    <PaginationEllipsis />
                  </PaginationItem>
                )
              }
              if (typeof pageNumber !== 'number') {
                return null
              }
              return (
                <PaginationItem
                  id={`pagination-${pageNumber}`}
                  key={pageNumber}
                  className={cn(styling.pagination.Item, {
                    [styling.pagination.currentPage]: pageNumber == currentPage,
                  })}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </PaginationItem>
              )
            })}
            <PaginationItem
              id="pagination-next"
              className={cn(styling.pagination.cheveron, {
                [styling.pagination.disabledPaginationItem]:
                  currentPage === lastPage,
              })}
              onClick={onNext}
            >
              <ChevronRight size={24} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  )

  const mobile = (
    <div className={cn('flex flex-col items-center gap-6', className)}>
      <MapleSelect
        id="pagination-items-per-page-select"
        className="w-fit rounded bg-neutral-100 px-4 py-2"
        placeholder={replacePlaceholders(t('general.showing'), {
          itemsCount: itemsPerPage ?? 24,
        })}
        defaultValue={
          itemsPerPage.toString() ?? itemsPerPageOptions[0]?.toString()
        }
        options={itemsPerPageOptions.map((value) => ({
          key: value.toString(),
          value: replacePlaceholders(t('general.showing'), {
            itemsCount: value,
          }),
        }))}
        onValueChange={(value) => {
          setPageInfo(1, parseInt(value), sortOption)
        }}
      />

      <div>
        <Pagination>
          <PaginationContent className="flex items-center gap-2.5 -space-x-px">
            <PaginationItem
              id="pagination-previous"
              key="previous"
              className={cn(styling.pagination.cheveron, {
                [styling.pagination.disabledPaginationItem]: currentPage === 1,
              })}
              onClick={onPrevious}
            >
              <ChevronLeft size={24} />
            </PaginationItem>
            {paginationRange?.map((pageNumber, index) => {
              if (pageNumber === DOTS) {
                // There should only be at most 2 ellipsis at the same index so this won't break react key requirement
                return (
                  <PaginationItem
                    key={`${DOTS}-${index}`}
                    className={cn(styling.pagination.ellipsis, 'px-0')}
                  >
                    <PaginationEllipsis />
                  </PaginationItem>
                )
              }
              if (typeof pageNumber !== 'number') {
                return null
              }
              return (
                <PaginationItem
                  id={`pagination-${pageNumber}`}
                  key={pageNumber}
                  className={cn(styling.pagination.Item, {
                    [styling.pagination.currentPage]: pageNumber == currentPage,
                  })}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </PaginationItem>
              )
            })}
            <PaginationItem
              id="pagination-next"
              className={cn(styling.pagination.cheveron, {
                [styling.pagination.disabledPaginationItem]:
                  currentPage === lastPage,
              })}
              onClick={onNext}
            >
              <ChevronRight size={24} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  )

  return (
    <ResponsiveComponent mobileComponent={mobile} desktopComponent={desktop} />
  )
}
